import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import Paragraph from '../../../atoms/paragraphs/Paragraph';
import Span from '../../../atoms/spans/Span';
import Button from '../../../atoms/buttons/Button';
import Menu, { MENU_CATEGORY } from '../../../organisms/menu/Menu';
import SelectWithValues from '../../../atoms/selects/SelectWithValues';
import RadioButtonLabel from '../../../molecules/labels/RadioButtonLabel';
import { ExportSaftHelper } from './helper';
import { SAFTService } from '../../../../services/SAFTService';
import HandleCallResultModal from '../../feedback/modal/HandleCallResultModal';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import moment from 'moment';
import NotAllowedLayout from '../../feedback/not-allowed/NotAllowedLayout';
import { AppContext } from '../../../../contexts/AppContext';

const ExportSaftLayout = (props) => {
  const { appState } = useContext(AppContext);
  const defaultFormData = ExportSaftHelper.getInitialState(props.intl);
  const [years, setYears] = useState([
    { key: `${moment().year()}`, value: moment().year() },
  ]);
  const [form, setForm] = useState(defaultFormData);
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [callSucceeded, setCallSucceeded] = useState(false);
  const [callResponse, setCallResponse] = useState(null);

  const populateDays = () => {
    const monthKey = ExportSaftHelper.getMonths(props.intl).find(
      (el) => el.value === form?.month.value
    );

    const monthNum = parseInt(monthKey.key.split('exportSAFTMonth')[1]);
    return ExportSaftHelper.getDaysInMonth(form?.year.value, monthNum);
  };

  const populateWeeks = () => {
    const monthKey = ExportSaftHelper.getMonths(props.intl).find(
      (el) => el.value === form?.month.value
    );

    const monthNum = parseInt(monthKey.value);
    return ExportSaftHelper.getWeeksInMonth(
      props.intl,
      form?.year.value,
      monthNum,
      props.intl.messages[monthKey.key]
    );
  };

  const [weeks, setWeeks] = useState(populateWeeks());
  const [days, setDays] = useState(populateDays());

  const onFormChange = (e, section) => {
    const value = e.target.value;

    let newForm = structuredClone(form);
    if (section === 'type') {
      newForm.type = value;
      newForm.period = ExportSaftHelper.PERIOD.MONTH;
    } else if (section === 'period') {
      newForm.period = value;
    } else if (section === 'year') {
      newForm.year = {
        key: parseInt(value),
        value: parseInt(value),
      };
      newForm.week = 1;
      newForm.day = 1;
    } else if (section === 'month') {
      newForm.month = {
        key: `exportSAFTMonth${parseInt(value)}`,
        value: parseInt(value),
      };
      newForm.week = 1;
      newForm.day = 1;
    } else if (section === 'week') {
      newForm.week = value;
    } else {
      newForm.day = parseInt(value);
    }

    setForm(newForm);
  };

  const onSubmit = async () => {
    setSubmitting(true);
    setIsSubmitted(true);
    setCallResponse('exportSAFTProcessing');
    const data = structuredClone(form);

    data.year = data.year.value.toString();

    if (data.type === 'guides') {
      delete data.week;
      delete data.day;
    }

    if (data.period === 'year') {
      delete data.month;
      delete data.day;
      delete data.week;
    } else if (data.period === 'month') {
      data.month = (data.month.value + 1).toString();
      delete data.day;
      delete data.week;
    } else if (data.period === 'week') {
      data.month = (data.month.value + 1).toString();
      data.day = data.week;
      delete data.week;
    } else {
      data.month = (data.month.value + 1).toString();
      delete data.week;
    }

    // Call API
    const params = new URLSearchParams(data).toString();

    const response = await SAFTService.exportSAFT(params);
    setSubmitting(false);

    if (!response.error) {
      // Handle api response
      const sanitizedResponse = DOMPurify.sanitize(response.message);
      const parsedResponse = parse(sanitizedResponse);
      setCallResponse(parsedResponse);
      setCallSucceeded(true);
    } else {
      // Handle api response
      const sanitizedResponse = DOMPurify.sanitize(response.error);
      const parsedResponse = parse(sanitizedResponse);
      setCallResponse(parsedResponse);
      setCallSucceeded(false);
    }
  };

  useEffect(() => {
    const fetchYears = async () => {
      const response = await SAFTService.getYears();

      if (!response.error) {
        let toAddYears = [];

        response.years.forEach((year) => {
          const addYear = {
            key: year,
            value: parseInt(year),
          };

          toAddYears.push(addYear);
        });

        const finaltoAddYears = toAddYears.filter(
          (year) => year.value !== moment().year()
        );

        setYears((prevYears) => [...prevYears, ...finaltoAddYears]);

        // Check if current month is January (0-indexed)
        if (moment().month() === 0) {
          if (finaltoAddYears.length > 0) {
            form.year = {
              key: finaltoAddYears[0].value,
              value: parseInt(finaltoAddYears[0].value),
            };

            form.month = {
              key: `exportSAFTMonth11`,
              value: 11,
            };

            setForm(form);
          } else {
            // When no previous years exist, reset to current year and January
            form.year = {
              key: moment().year(),
              value: moment().year(),
            };
            form.month = {
              key: `exportSAFTMonth0`,
              value: 0,
            };

            setForm(form);
          }
        }
      }
    };

    fetchYears();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setWeeks(populateWeeks());
    setDays(populateDays());

    // eslint-disable-next-line
  }, [form?.month, form?.year]);

  const canView = () => {
    if (appState.accountTrial === 'false') {
      return true;
    } else {
      return false;
    }
  };

  return (
    <div
      id='main-content'
      className='container --export-saft position-relative'
    >
      {!canView() && (
        <NotAllowedLayout
          title='notAllowedTitle'
          description='notAllowedDescription'
        />
      )}

      <div className='d-flex flex-wrap row mx-0 w-100'>
        <div
          data-testid='export-saft-layout'
          className='col-12 col-lg-9 ps-md-0'
        >
          <div className='d-flex flex-column w-100'>
            <div className='col-lg-10 px-0'>
              <Paragraph className='text-header h2'>
                {props.intl.messages['exportSAFTTitle']}
              </Paragraph>

              <Paragraph className='text-paragraph mt-3 mb-2'>
                <Span className='d-block'>
                  {props.intl.messages['exportSAFTDescription']}
                </Span>
              </Paragraph>
            </div>

            <div className='sep-light mt-2' />

            <div className='form-container d-flex flex-column w-100 mt-5'>
              <div className='d-flex flex-column w-100'>
                <label className='text-label mb-3'>
                  {props.intl.messages['exportSAFTDocs']}
                </label>
                <div className='d-flex w-100'>
                  <div className='option-container'>
                    <RadioButtonLabel
                      id='invoice_budget'
                      name='type'
                      className='checkbox round'
                      value={'all'}
                      checked={form?.type === 'all'}
                      onChange={(e) => onFormChange(e, 'type')}
                    >
                      {'exportSAFTInvoicesAndQuotes'}
                    </RadioButtonLabel>
                  </div>

                  <div className='option-container ms-3'>
                    <RadioButtonLabel
                      id='notes'
                      name='type'
                      className='checkbox round'
                      value={'guides'}
                      checked={form?.type === 'guides'}
                      onChange={(e) => onFormChange(e, 'type')}
                    >
                      {'exportSAFTNotes'}
                    </RadioButtonLabel>
                  </div>
                </div>
              </div>

              <div className='d-flex flex-column w-100 mt-5'>
                <label className='text-label mb-3'>
                  {props.intl.messages['exportSAFTPeriod']}
                </label>
                <div className='d-flex w-100'>
                  <div className='option-container'>
                    <RadioButtonLabel
                      id='period_annual'
                      name='period'
                      className='checkbox round'
                      value={'year'}
                      checked={form.period === 'year'}
                      onChange={(e) => onFormChange(e, 'period')}
                    >
                      {'exportSAFTPeriodAnnual'}
                    </RadioButtonLabel>
                  </div>

                  <div className='option-container ms-3'>
                    <RadioButtonLabel
                      id='period_monthly'
                      name='period'
                      className='checkbox round'
                      value={'month'}
                      checked={form.period === 'month'}
                      onChange={(e) => onFormChange(e, 'period')}
                    >
                      {'exportSAFTPeriodMonthly'}
                    </RadioButtonLabel>
                  </div>

                  {form?.type === 'all' && (
                    <div className='option-container ms-3'>
                      <RadioButtonLabel
                        id='period_weekly'
                        name='period'
                        className='checkbox round'
                        value={'week'}
                        checked={form.period === 'week'}
                        onChange={(e) => onFormChange(e, 'period')}
                      >
                        {'exportSAFTPeriodWeekly'}
                      </RadioButtonLabel>
                    </div>
                  )}

                  {form?.type === 'all' && (
                    <div className='option-container ms-3'>
                      <RadioButtonLabel
                        id='period_daily'
                        name='period'
                        className='checkbox round'
                        value={'day'}
                        checked={form.period === 'day'}
                        onChange={(e) => onFormChange(e, 'period')}
                      >
                        {'exportSAFTPeriodDaily'}
                      </RadioButtonLabel>
                    </div>
                  )}
                </div>
              </div>

              <div className='d-flex flex-column flex-sm-row col-lg-10 px-0 mt-5 period-selector-container'>
                <div className='col period-selector'>
                  <label className='text-label mb-3'>
                    {props.intl.messages['exportSAFTYearLabel']}
                  </label>
                  <SelectWithValues
                    id='year'
                    key='year'
                    options={years}
                    value={form.year.value}
                    onChange={(e) => onFormChange(e, 'year')}
                  />
                </div>

                {((form?.type === 'all' && form?.period !== 'year') ||
                  (form?.type === 'guides' && form?.period !== 'year')) && (
                  <div className='col period-selector'>
                    <label className='text-label mb-3'>
                      {props.intl.messages['exportSAFTMonthLabel']}
                    </label>
                    <SelectWithValues
                      id='month'
                      key='month'
                      options={ExportSaftHelper.getMonths(props.intl)}
                      value={form.month.value}
                      onChange={(e) => onFormChange(e, 'month')}
                    />
                  </div>
                )}

                {form?.type === 'all' && form?.period === 'week' && (
                  <div className='col period-selector'>
                    <label className='text-label mb-3'>
                      {props.intl.messages['exportSAFTWeekLabel']}
                    </label>
                    <SelectWithValues
                      id='week'
                      key='week'
                      options={weeks}
                      value={form?.week}
                      onChange={(e) => onFormChange(e, 'week')}
                    />
                  </div>
                )}

                {form?.type === 'all' && form?.period === 'day' && (
                  <div className='col period-selector'>
                    <label className='text-label mb-3'>
                      {props.intl.messages['exportSAFTDailyLabel']}
                    </label>
                    <SelectWithValues
                      id='day'
                      key='day'
                      options={days}
                      value={form?.day}
                      onChange={(e) => onFormChange(e, 'day')}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className='d-flex col-12 col-lg-10 px-0 mt-4'>
              <div className='d-flex w-100 flex-column flex-sm-row justify-content-end'>
                <div
                  data-testid='saft_version'
                  className='d-flex saft-version me-4 mb-4 my-sm-auto'
                >
                  {form?.type === 'all' && (
                    <span className='text-paragraph'>
                      <FormattedMessage
                        id='exportSAFTInvoicesVersion'
                        values={{
                          b: (chunks) => <b>{chunks}</b>,
                        }}
                      />
                    </span>
                  )}

                  {form?.type === 'guides' && (
                    <span className='text-paragraph'>
                      <FormattedMessage
                        id='exportSAFTNotesVersion'
                        values={{
                          b: (chunks) => <b>{chunks}</b>,
                        }}
                      />
                    </span>
                  )}
                </div>

                <Button
                  className='button button-primary ms-auto ms-sm-0'
                  id={'save_button'}
                  label={props.intl.messages['exportSAFTButton']}
                  onClick={onSubmit}
                  disabled={submitting}
                />
              </div>
            </div>
          </div>
        </div>

        <div className='col-lg-3 pe-md-0 mt-5'>
          <Menu {...props} activeCategory={MENU_CATEGORY.AT_COMUNICATION} />
        </div>
      </div>

      {isSubmitted && (
        <HandleCallResultModal
          isLoading={submitting}
          succeeded={callSucceeded}
          message={callResponse}
          onClose={() => {
            setIsSubmitted(false);
            setCallResponse(null);
          }}
          showCloseButton={true}
        />
      )}
    </div>
  );
};

export default injectIntl(ExportSaftLayout);
